<template>
  <div id="root" ref="imageGallery" class="image-gallery" :class="{ 'overflow-hidden': overflowHidden }">
    <slot name="title"> </slot>
    <div ref="sliderRoot" class="image-gallery-slider" :class="{ 'image-gallery-slider-expanded': expanded }">
      <transition name="fade">
        <ImageGalleryArrow
          :left="true"
          :background="!expanded"
          :style="{
            left: arrowPositionPx,
          }"
          :class="{
            'position-left': expanded,
            hidden: !showArrow,
          }"
          :disabled="slidePosition <= indexZeroSlidePosition"
          class="images-nav-arrow"
          @click.passive="moveLeft()"
        ></ImageGalleryArrow>
      </transition>
      <transition name="fade">
        <ImageGalleryArrow
          :right="true"
          :background="!expanded"
          :style="{
            right: arrowPositionPx,
          }"
          :class="{
            'position-right': expanded,
            hidden: !showArrow,
          }"
          :disabled="slidePosition >= translatedWidth"
          class="images-nav-arrow"
          @click.passive="moveRight()"
        ></ImageGalleryArrow>
      </transition>
      <div
        ref="images"
        class="images"
        :class="{
          transition: moving && !sliding,
          sliding: sliding,
        }"
        :style="{
          width: imagesWidthPx,
          transform: slidePositionPx,
        }"
        @click="onCardCloseClicked.bind(this)"
      >
        <template v-for="(image, index) in filteredImages" :key="image.album_photo_id">
          <ImageGalleryCard
            :image="image"
            :max-image-width="maxImageWidth"
            :expanded="expanded"
            :current-index="currentImageIndex"
            :index="index"
            :on-card-close-clicked="onCardCloseClicked"
            :sku-id="skuId"
            :active="image.id === activeId"
            :interacted="interacted"
            :share-title="shareTitle"
            @click.passive="onCardClicked(index)"
            @visible="onCardVisible(index)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-restricted-syntax */
import { nextTick } from 'vue';
import VueScrollTo from 'vue-scrollto';
import CSSMatrix from 'CSSMatrix';
import ImageGalleryCard from '@/components/ugc/ImageGalleryCard.vue';
import ImageGalleryArrow from '@/components/ugc/ImageGalleryArrow.vue';
import 'intersection-observer';

import Tracking from '@/components/commons/Tracking.js';

import { importScript } from '@/components/commons/utils.js';

export default {
  name: 'ImageGallery',
  components: {
    ImageGalleryCard,
    ImageGalleryArrow,
  },
  props: {
    source: String,
    type: String,
    apiUrl: String,
    widgetId: [String, Number],
    skuId: [String, Number],
    shareTitle: String,
    customContainerClass: String,
    images: {
      type: Array,
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    hasMore: {
      type: [Boolean, Number],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    overflowHidden: {
      type: Boolean,
      default: true,
    },
    customArrowMarginPx: Number,
  },
  data() {
    return {
      containerClass: this.customContainerClass || 'container',
      page: 1,
      imagesWidth: 0,
      translatedWidth: 0,
      indexZeroSlidePosition: 0,
      slidePosition: 0,
      moving: false,
      imagesNavShow: true,
      expanded: false,
      blockWidth: 0,
      imageWidth: 0,
      maxImageWidth: 240,
      maxImageWidthThreshold: 600,
      currentImageIndex: null,
      activeId: -1,
      touch: {
        started: false,
        startX: 0,
        startY: 0,
        previousX: 0,
        previousY: 0,
      },
      scrollingSpeed: 500,
      positionOffSet: 14,
      arrowPosition: this.customArrowMarginPx,
      showArrow: true,
      currentVelocity: 0,
      sliding: false,
      socialMediaId: -1,
      step: 0,
      interacted: false,
      arrowBlocksWidth: 32,
      intervalTransition: null,
      breakPointMobile: 845,
      breakPointDesktop: 1300,
      isVisible: false,
    };
  },
  computed: {
    imagesWidthPx() {
      return `${this.imagesWidth}px`;
    },
    imageHeightPx() {
      return `${this.imageHeight}px !important`;
    },
    slidePositionPx() {
      if (this.slidePosition < this.indexZeroSlidePosition && !this.expanded) {
        this.slidePosition = this.indexZeroSlidePosition;
      }

      return `translate3d(${-this.slidePosition}px, 0px, 0px)`;
    },
    arrowBlocksWidthPx() {
      return `${this.arrowBlocksWidth}px`;
    },
    arrowPositionPx() {
      if (this.arrowPosition !== null) {
        return `${this.arrowPosition}px`;
      }
      return null;
    },
    filteredImages() {
      return this.images.reduce((acc, image) => {
        const found = !!acc.find((item) => item.album_photo_id == image.album_photo_id);
        if (!found) {
          acc.push(image);
        }
        return acc;
      }, []);
    },
  },
  watch: {
    currentImageIndex() {
      if (this.currentImageIndex !== null) {
        this.updatePositionWithIndex();
      }
    },
    expanded(currentValue, prevValue) {
      if (currentValue !== prevValue) {
        this.$emit('expandedVisible', this.isVisible && currentValue);
        if (this.isVisible && currentValue) {
          this.$emit('interacted');
        }
        if (currentValue) {
          this.$nextTick(() => this.scroll());
        }
      }
    },
    async skuId(value) {
      if (!value) return;

      this.expanded = false;
    },
    images() {
      this.$nextTick(() => {
        this.updateMaxImageWidth();
        this.updateImagesWidth();
      });
    },
  },
  async mounted() {
    window.addEventListener('resize', this.onWindowResize, { passive: true });
    window.addEventListener('orientationchange', this.setTimeoutWindowResize, { passive: true });
    this.setObserver();

    this.setImageSize();
    this.shouldShowImagesNav();
    this.updateArrowSize();
    this.updateIndexZeroSlidePosition();
    this.updateStepSize();
    this.updateMaxImageWidth();
    this.updateImagesWidth();

    if (!this.interacted) {
      this.slidePosition = this.indexZeroSlidePosition;
    }

    if (this.socialMediaId !== -1 && this.filteredImages.length) {
      this.onCardClicked(0);
    }

    if (this.$refs.images) {
      this.$refs.images.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.$refs.images.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.$refs.images.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    importScript('//assets.pinterest.com/sdk/sdk.js');
  },
  beforeUnmount() {
    if (this.$refs.images) {
      this.$refs.images.removeEventListener('touchstart', this.onTouchStart);
      this.$refs.images.removeEventListener('touchmove', this.onTouchMove);
      this.$refs.images.removeEventListener('touchend', this.onTouchEnd);
    }
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.setTimeoutWindowResize);
    this.clearObserver();
  },
  unmounted() {
    this.cancelIntervalTransition();
  },
  methods: {
    setObserver() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.length) {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.isVisible = true;
            Tracking.track('ugc_viewed');
          } else {
            this.isVisible = false;
          }
          this.$emit('expandedVisible', this.isVisible && this.expanded);
        }
      });

      this.observer.observe(this.$refs.sliderRoot);
    },
    clearObserver() {
      this.observer.unobserve(this.$refs.sliderRoot);
    },
    async checkUpdateData() {
      if (!this.hasMore) return;

      if (!this.loading) {
        this.$emit('loadMore');
      }
    },
    setImageSize() {
      if (this.isMobileAndSmaller()) {
        this.imageWidth = 160;
        this.imageHeight = 160;
      } else {
        this.imageWidth = 240;
        this.imageHeight = 240;
      }
    },
    updateMaxImageWidth() {
      if (this.$refs.sliderRoot) {
        let maxImageWidth = this.$refs.sliderRoot.clientWidth;
        if (this.isMobileAndGreater()) {
          maxImageWidth -= 480;
          if (maxImageWidth > 800) maxImageWidth = 800;
        }

        if (maxImageWidth >= window.innerHeight) {
          maxImageWidth = window.innerHeight - 60;
        }

        this.maxImageWidth = maxImageWidth;

        if (this.isMobileAndGreater()) {
          this.blockWidth = this.expanded ? this.maxImageWidth + 300 : this.imageWidth;
        } else {
          this.blockWidth = this.expanded ? this.maxImageWidth : this.imageWidth;
        }
      }
    },
    updateImagesWidth() {
      if (this.$refs.sliderRoot) {
        const imageWidth = this.blockWidth;
        this.imagesWidth = imageWidth * this.filteredImages.length;

        const currentViewWidth = this.$refs.sliderRoot.clientWidth;
        this.translatedWidth = Math.round(this.filteredImages.length * imageWidth - currentViewWidth);
      }
    },
    moveRight() {
      this.interacted = true;
      this.$emit('interacted');
      this.moving = true;
      this.sliding = false;
      if (this.expanded) {
        if (this.currentImageIndex < this.filteredImages.length - 1) {
          this.activeId = this.filteredImages[++this.currentImageIndex].id;
        }
      } else {
        let { slidePosition } = this;
        slidePosition += this.step;
        if (slidePosition > this.translatedWidth) slidePosition = this.translatedWidth;
        this.slidePosition = slidePosition;
      }
    },
    moveLeft() {
      this.interacted = true;
      this.$emit('interacted');
      this.moving = true;
      this.sliding = false;
      if (this.expanded) {
        if (this.currentImageIndex) {
          this.activeId = this.filteredImages[--this.currentImageIndex].id;
        }
      } else {
        this.slidePosition -= this.step;
        if (this.slidePosition < this.indexZeroSlidePosition) {
          this.slidePosition = this.indexZeroSlidePosition;
        }
      }
    },
    onTouchStart(event) {
      this.slidePosition = this.getImagesTranslateX();
      const touch = event.targetTouches[0];
      this.touch = {
        started: true,
        startX: touch.clientX,
        startY: touch.clientY,
        previousX: touch.clientX,
        previousY: touch.clientY,
        previousTime: Date.now(),
      };
    },
    onTouchMove(event) {
      this.sliding = false;
      const { clientX, clientY } = event.targetTouches[0];
      const xDist = clientX - this.touch.previousX;
      const yDist = clientY - this.touch.previousY;

      // User is swiping horizontally
      if (Math.abs(xDist) > Math.abs(yDist)) {
        // event.cancelable && event.preventDefault();

        if (!this.expanded) {
          let slidePositionTemp = this.slidePosition;
          slidePositionTemp += this.touch.previousX - clientX;
          slidePositionTemp = slidePositionTemp < this.indexZeroSlidePosition ? this.indexZeroSlidePosition : slidePositionTemp;
          slidePositionTemp = slidePositionTemp > this.translatedWidth ? this.translatedWidth : slidePositionTemp;
          this.slidePosition = slidePositionTemp;
        } else {
          if (this.currentImageIndex === 0 && xDist > 0) return;
          if (this.currentImageIndex === this.filteredImages.length - 1 && xDist < 0) return;
          this.slidePosition += this.touch.previousX - clientX;
        }

        const currentTime = Date.now();
        this.currentVelocity = Math.round(((this.touch.previousX - clientX) / (currentTime - this.touch.previousTime)) * 100);

        this.touch = {
          ...this.touch,
          previousX: clientX,
          previousY: clientY,
          previousTime: currentTime,
        };
      }
    },
    onTouchEnd(event) {
      this.touch.started = false;
      this.interacted = true;
      this.$emit('interacted');
      const { clientX, clientY } = event.changedTouches[0];
      if (this.expanded) {
        if (Math.abs(clientX - this.touch.startX) > Math.abs(clientY - this.touch.startY) && Math.abs(clientX - this.touch.startX) >= 50) {
          this.moving = true;
          const diff = this.touch.startX - clientX;
          if (diff > 0 && this.currentImageIndex < this.filteredImages.length - 1) {
            this.activeId = this.filteredImages[++this.currentImageIndex].id;
          }
          if (diff < 0 && this.currentImageIndex > 0) {
            this.activeId = this.filteredImages[--this.currentImageIndex].id;
          }
          if (this.isMobileAndSmaller()) {
            this.scroll();
          }
        } else {
          this.updatePositionWithIndex();
        }
      } else {
        const currentTime = Date.now();

        if (currentTime - this.touch.previousTime < 30) {
          this.startMomemtum();
        }
      }
    },
    onWindowResize() {
      this.setImageSize();
      this.updateMaxImageWidth();
      this.updateImagesWidth();
      this.shouldShowImagesNav();
      this.updatePositionWithIndex();
      this.updateArrowSize();
      this.updateIndexZeroSlidePosition();
      this.updateStepSize();
    },
    setTimeoutWindowResize() {
      setTimeout(this.onWindowResize, 250);
    },
    shouldShowImagesNav() {
      this.imagesNavShow = !this.isMobileAndSmaller();
    },
    onCardClicked(index) {
      if (this.expanded) return;
      this.cancelIntervalTransition();
      this.expanded = true;
      this.moving = false;
      this.sliding = false;
      this.currentImageIndex = index;
      this.activeId = this.filteredImages[this.currentImageIndex].id;
      this.updateMaxImageWidth();
      this.updateImagesWidth();
      this.updateArrowSize();
    },
    onCardCloseClicked(e) {
      e.stopPropagation();
      this.cancelIntervalTransition();
      nextTick().then(() => {
        this.expanded = false;
        this.updateMaxImageWidth();
        this.updateImagesWidth();
        const { imageWidth } = this;
        this.moving = false;
        this.slidePosition = imageWidth * this.currentImageIndex + this.indexZeroSlidePosition;
        this.slidePosition = this.slidePosition > this.translatedWidth ? this.translatedWidth : this.slidePosition;
        this.currentImageIndex = null;
        this.arrowBlocksWidth = 32;
        this.updateArrowSize();
        this.scroll(-220);
      });
    },
    onCardVisible(index) {
      if (index >= this.filteredImages.length - 3) {
        this.checkUpdateData();
      }
    },
    updatePositionWithIndex() {
      if (this.expanded) {
        const width = this.blockWidth;
        let slidePosition = width * this.currentImageIndex;
        const diff = (this.$refs.sliderRoot.clientWidth - width) / 2;
        slidePosition -= diff;
        this.slidePosition = slidePosition;
      }
    },
    updateIndexZeroSlidePosition() {
      if (this.expanded) return;
      this.indexZeroSlidePosition = 0;
    },
    updateArrowSize() {
      if (this.expanded) {
        const width = this.$refs.sliderRoot.clientWidth;
        let block = width - this.blockWidth;
        if (this.isMobileAndGreater()) {
          block -= 64;
        } else {
          block -= 44;
        }
        block /= 2;
        if (block > this.maxImageWidth) {
          this.arrowBlocksWidth = this.maxImageWidth;
          this.arrowPosition = block - this.maxImageWidth + 12;
        } else {
          this.arrowBlocksWidth = block;
          this.arrowPosition = this.customArrowMarginPx;
        }
      } else {
        this.arrowPosition = this.customArrowMarginPx;
      }

      if (this.isMobileAndSmaller() && this.expanded) {
        this.showArrow = false;
      } else {
        this.showArrow = true;
      }
    },
    updateStepSize() {
      this.step = this.imageWidth * 2;
    },
    startMomemtum() {
      const transition = (t) => t * (2 - t);
      this.currentVelocity = this.currentVelocity > 200 ? 200 : this.currentVelocity;
      if (this.slidePosition > this.indexZeroSlidePosition && this.slidePosition < this.translatedWidth) {
        this.sliding = true;
        this.currentVelocity *= transition(0.6);
        if (Math.abs(this.currentVelocity) < 2) this.currentVelocity = 0;

        let { slidePosition } = this;
        slidePosition += this.currentVelocity;
        slidePosition = slidePosition < this.indexZeroSlidePosition ? this.indexZeroSlidePosition : slidePosition;
        slidePosition = slidePosition > this.translatedWidth ? this.translatedWidth : slidePosition;
        this.slidePosition = slidePosition;

        if (Math.abs(this.currentVelocity) > 2) {
          this.startMomemtum();
        }
      }
    },
    scroll(offset = 6) {
      VueScrollTo.scrollTo(this.$refs.sliderRoot, this.scrollingSpeed, {
        container: 'body',
        offset,
      });
    },
    cancelIntervalTransition() {
      clearInterval(this.intervalTransition);
      this.intervalTransition = null;
    },
    getImagesTranslateX() {
      let pos = this.indexZeroSlidePosition;
      if (this.$refs.images) {
        const c = window.getComputedStyle(this.$refs.images);
        const matrix = new CSSMatrix(c.transform);
        pos = -matrix.m41;
      }
      return pos;
    },
    isMobileAndSmaller() {
      return window.matchMedia(`(max-width: ${this.breakPointMobile - 1}px)`).matches;
    },
    isMobileAndGreater() {
      return window.matchMedia(`(min-width: ${this.breakPointMobile}px)`).matches;
    },
    isDesktopAndGreater() {
      return window.matchMedia(`(min-width: ${this.breakPointDesktop}px)`).matches;
    },
    emitEvent(name, value) {
      this.$eventHub && this.$eventHub.$emit(name, value);
      this.$emit(name, value);
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.image-gallery-slider {
  position: relative;
  box-sizing: border-box;
  padding: 0px;

  &, 
  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  .images {
    padding: 12px 0;
    display: flex;
    align-items: flex-start;
    user-select: none;
    touch-action: none;
    &.transition {
      transition: transform 150ms ease-out;
    }

    &.sliding {
      transition: transform 1000ms ease-out;
    }
  }

  .images-nav-arrow {
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      display: none;
    }
  }

  /* purgecss ignore */
  &:hover {
    .arrow {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s linear, opacity 200ms ease-out;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 250ms linear;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}

.image-gallery {
  &.overflow-hidden {
    @media (min-width: $mobile-breakpoint) {
      overflow: hidden;
    }
  }
}
</style>
